import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Avatar, Box, Typography } from "@mui/material";
import { wsUrls } from "../../utils/urls";
import { messagingNotifyWsMessageTypes } from "../../constants/websockets";
import useWebSocket from "./useWebSocket";

let suppressInAppMessageBanners = false;
let latestMessagingUnreadCount = null;
const unreadCountSubscribers = new Set();

export function setSuppressInAppMessageBanners(shouldSuppress) {
  suppressInAppMessageBanners = Boolean(shouldSuppress);
}

function publishUnreadCount(totalUnreadCount) {
  latestMessagingUnreadCount = totalUnreadCount;
  unreadCountSubscribers.forEach((subscriber) => {
    subscriber(totalUnreadCount);
  });
}

export function subscribeToMessagingUnreadCount(subscriber) {
  if (typeof subscriber !== "function") {
    return () => {};
  }
  unreadCountSubscribers.add(subscriber);
  if (typeof latestMessagingUnreadCount === "number") {
    subscriber(latestMessagingUnreadCount);
  }
  return () => {
    unreadCountSubscribers.delete(subscriber);
  };
}

export default function useMessagingNotifyWebSocket({ enabled = true }) {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  const renderNotificationContent = useCallback((message) => {
    const senderName =
      typeof message.title === "string" && message.title.trim()
        ? message.title.trim()
        : "New message";
    const messageText =
      typeof message.message === "string" && message.message.trim()
        ? message.message.trim()
        : "You received a new message.";
      
    const truncatedMessageText = messageText.length > 50 ? messageText.substring(0, 50) + "..." : messageText;
    const channelTitle =
      typeof message.channel_title === "string" && message.channel_title.trim()
        ? message.channel_title.trim()
        : "";
    const senderImage =
      typeof message.sender_image === "string" ? message.sender_image : "";
    const unreadCount =
      typeof message.total_unread_count === "number"
        ? message.total_unread_count
        : null;
    const unreadLabel =
      unreadCount !== null
        ? `${unreadCount} unread ${unreadCount === 1 ? "message" : "messages"}`
        : "";

    return (
      <Box
        sx={{
          width: 380,
          maxWidth: "calc(100vw - 24px)",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "rgba(59, 130, 246, 0.45)",
          background:
            "linear-gradient(140deg, rgba(30,41,59,0.96), rgba(15,23,42,0.98))",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.4)",
          px: 1.5,
          py: 1.25,
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start",
        }}
      >
        <Avatar
          src={senderImage}
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            color: "common.white",
            fontWeight: 700,
            border: "2px solid rgba(255,255,255,0.24)",
          }}
        >
          {senderName.slice(0, 1).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            sx={{
              color: "common.white",
              fontSize: "0.92rem",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {senderName}
          </Typography>
          {channelTitle ? (
            <Typography
              sx={{
                color: "rgba(147, 197, 253, 1)",
                fontSize: "0.78rem",
                fontWeight: 600,
                mt: 0.25,
              }}
            >
              {channelTitle}
            </Typography>
          ) : null}
          <Typography
            sx={{
              color: "rgba(226, 232, 240, 1)",
              fontSize: "0.83rem",
              mt: 0.5,
              wordBreak: "break-word",
            }}
          >
            {truncatedMessageText}
          </Typography>
          {unreadLabel ? (
            <Typography
              sx={{
                color: "rgba(253, 224, 71, 1)",
                fontSize: "0.74rem",
                fontWeight: 700,
                mt: 0.65,
                letterSpacing: "0.01em",
                textTransform: "uppercase",
              }}
            >
              {unreadLabel}
            </Typography>
          ) : null}
        </Box>
      </Box>
    );
  }, []);

  const handleMessage = useCallback((message) => {
    switch (message.type) {
      case "subscribed":
        setConnectionStatus("connected");
        break;
      case "unsubscribed":
        setConnectionStatus("disconnected");
        break;
      case "notify":
      case "notify_update": {
        const unreadCount = message.total_unread_count;
        if (typeof unreadCount === "number") {
          setTotalUnreadCount(unreadCount);
          publishUnreadCount(unreadCount);
        }

        if (suppressInAppMessageBanners) {
          break;
        }
        enqueueSnackbar("New message", {
          anchorOrigin: {
            horizontal: "left",
            vertical: "top",
          },
          autoHideDuration: 5500,
          content: () => renderNotificationContent(message),
        });
        break;
      }
      case "error":
        setConnectionStatus("error");
        break;
      default:
        break;
    }
  }, [renderNotificationContent]);

  const { send, isOpen, isReady } = useWebSocket({
    path: wsUrls.messaging.notifications(),
    enabled,
    messageTypes: messagingNotifyWsMessageTypes,
    autoReconnect: true,
    onMessage: handleMessage,
    onOpen: useCallback(() => {
      setConnectionStatus("connecting");
    }, []),
    onClose: useCallback(() => {
      setConnectionStatus("disconnected");
    }, []),
    onError: useCallback(() => {
      setConnectionStatus("error");
    }, []),
  });

  useEffect(() => {
    if (isOpen && isReady && connectionStatus === "connecting") {
      send({ type: "subscribe" });
    }
  }, [connectionStatus, isOpen, isReady, send]);

  return {
    connectionStatus,
    totalUnreadCount,
  };
}
